import { replyTargets } from "@/nostr/api/feed"
import { KINDS, type NostrEvent, type NostrFilter } from "@/nostr/types"

/**
 * Engagement axis of the unified filter architecture.
 *
 * Counts are built only from interaction events (kind 7 reactions, kind 6
 * reposts and kind 1 replies) that the client has actually seen. Nothing is
 * estimated: a post whose interactions were not delivered by the connected
 * relays simply has a low count, which is why the UI labels these numbers as
 * "counts observed from the connected relays" instead of exact totals.
 *
 * The index is a pure function of a list of events, so it can be unit tested and
 * reused by the feeds, the search results and the bookmarks page.
 */
export interface EngagementCounts {
  likes: number
  reposts: number
  replies: number
  /** likes + reposts + replies */
  total: number
}

export const EMPTY_ENGAGEMENT: EngagementCounts = { likes: 0, reposts: 0, replies: 0, total: 0 }

/** `noteId` -> counts */
export type EngagementIndex = Map<string, EngagementCounts>

export interface EngagementThresholds {
  minLikes?: number
  minReposts?: number
  minReplies?: number
  minTotalEngagement?: number
}

/** Every `e` tag target of an event. */
function targetsOf(event: NostrEvent): string[] {
  const targets: string[] = []
  for (const tag of event.tags) {
    if (tag[0] === "e" && tag[1] && !targets.includes(tag[1])) targets.push(tag[1])
  }
  return targets
}

function bump(index: EngagementIndex, id: string, field: keyof Omit<EngagementCounts, "total">): void {
  const current = index.get(id) ?? { ...EMPTY_ENGAGEMENT }
  current[field] += 1
  current.total = current.likes + current.reposts + current.replies
  index.set(id, current)
}

/**
 * Tallies interactions per target note.
 *
 * - one reaction per author counts (NIP-25 allows an author to change emoji);
 * - one repost per author counts;
 * - a reply only counts for its **direct parent** (NIP-10 `reply` marker), not
 *   for the whole thread;
 * - duplicated events (same id from several relays) are counted once.
 */
export function buildEngagementIndex(events: readonly NostrEvent[]): EngagementIndex {
  const index: EngagementIndex = new Map()
  const seenEvents = new Set<string>()
  const seenLikes = new Set<string>()
  const seenReposts = new Set<string>()

  for (const event of events) {
    if (!event?.id || seenEvents.has(event.id)) continue
    seenEvents.add(event.id)

    if (event.kind === KINDS.REACTION) {
      for (const target of targetsOf(event)) {
        const key = `${target}#${event.pubkey}`
        if (seenLikes.has(key)) continue
        seenLikes.add(key)
        bump(index, target, "likes")
      }
      continue
    }

    if (event.kind === KINDS.REPOST) {
      for (const target of targetsOf(event)) {
        const key = `${target}#${event.pubkey}`
        if (seenReposts.has(key)) continue
        seenReposts.add(key)
        bump(index, target, "reposts")
      }
      continue
    }

    if (event.kind === KINDS.NOTE) {
      const parent = replyTargets(event).replyId
      if (parent) bump(index, parent, "replies")
    }
  }

  return index
}

/** Counts for one note; never `undefined`, so callers do not need guards. */
export function engagementOf(index: EngagementIndex | undefined, noteId: string): EngagementCounts {
  return index?.get(noteId) ?? EMPTY_ENGAGEMENT
}

/** True when every threshold is met (an index that is missing counts as zero). */
export function matchesEngagement(
  noteId: string,
  index: EngagementIndex | undefined,
  thresholds: EngagementThresholds,
): boolean {
  const counts = engagementOf(index, noteId)
  if ((thresholds.minLikes ?? 0) > 0 && counts.likes < (thresholds.minLikes ?? 0)) return false
  if ((thresholds.minReposts ?? 0) > 0 && counts.reposts < (thresholds.minReposts ?? 0)) return false
  if ((thresholds.minReplies ?? 0) > 0 && counts.replies < (thresholds.minReplies ?? 0)) return false
  if ((thresholds.minTotalEngagement ?? 0) > 0 && counts.total < (thresholds.minTotalEngagement ?? 0)) {
    return false
  }
  return true
}

/** True when at least one threshold is active. */
export function hasEngagementThreshold(thresholds: EngagementThresholds): boolean {
  return (
    (thresholds.minLikes ?? 0) > 0 ||
    (thresholds.minReposts ?? 0) > 0 ||
    (thresholds.minReplies ?? 0) > 0 ||
    (thresholds.minTotalEngagement ?? 0) > 0
  )
}

const MAX_FILTER_IDS = 50
const MAX_LIMIT = 500

/**
 * One REQ per relay for the whole visible page: three filters (`#e` reactions,
 * reposts and replies) per id chunk instead of one subscription per note.
 */
export function engagementFiltersForIds(ids: readonly string[]): NostrFilter[] {
  const unique = [...new Set(ids.filter(Boolean))]
  const filters: NostrFilter[] = []
  for (let index = 0; index < unique.length; index += MAX_FILTER_IDS) {
    const chunk = unique.slice(index, index + MAX_FILTER_IDS)
    filters.push({ kinds: [KINDS.REACTION], "#e": chunk, limit: Math.min(MAX_LIMIT, chunk.length * 6) })
    filters.push({ kinds: [KINDS.REPOST], "#e": chunk, limit: Math.min(MAX_LIMIT, chunk.length * 3) })
    filters.push({ kinds: [KINDS.NOTE], "#e": chunk, limit: Math.min(MAX_LIMIT, chunk.length * 3) })
  }
  return filters
}
